import { DispatchCustomEventMessageKey } from '@infomaniak/ksuite-bridge';
import {
  Component,
  Element,
  forceUpdate,
  Fragment,
  h,
  Host,
  Listen,
  State,
} from '@stencil/core';

import wcShared from '@web-components/shared';

import { FocusedService } from '../services/focusedService.abstract';
import { globalStore } from '../store/global.store';
import {
  ALL_LAYOUT_CONFIG,
  FALL_BACK_LAYOUT,
} from '../store/layout/config/config.constant';
import { USER_ACCOUNT } from '../store/layout/constant';
import {
  LayoutStore,
  setCurrentLayoutAndDefaultLayoutPage,
} from '../store/layout/layout.store';
import {
  TSettingsHeaderOptions,
  TSettingsLayout,
  TSettingsLayoutOptions,
  TSettingsLayoutPage,
} from '../store/layout/layout.type';
import {
  flushStore,
  getServiceFromLayout,
} from '../store/service/service.store';
import { ProjectNames } from '../store/service/service.type';
import {
  getLayoutFromLayoutPage,
  getServiceFromProjectName,
  isLayoutAndNotDisabled,
  isLayoutPageAndNotDisabled,
} from '../utils/projectUtils';

@Component({
  tag: 'module-settings-component',
  styleUrl: 'main.scss',
  shadow: true,
})
export class Main {
  @Element() host: HTMLModuleSettingsComponentElement;
  @State() isSettingsOpen = false;

  componentDidLoad() {
    const params = new URLSearchParams(window.location.search);
    const globalSettings = params.get('global-settings');
    const selectedId = params.get('selected-id');
    if (globalSettings)
      void this.openSettingsHandler({
        detail: [globalSettings, null, { selectedId }],
      } as unknown as CustomEvent<string[]>);
  }

  @Listen('wcSettingsUpdateFromApp', { target: 'document' })
  wcSettingsUpdateFroApp(
    e: CustomEvent<
      object & {
        project: ProjectNames;
        method: 'PATCH' | 'POST' | 'PUT' | 'DELETE';
      }
    >,
  ) {
    const { project = '', method = 'PUT', ...change } = e.detail;
    const service = getServiceFromProjectName(project);
    Object.keys(change).forEach((key) => {
      const store = service.store.state;
      void service[method.toLowerCase()](
        { detail: { type: key, value: change[key] } },
        typeof store[key] === 'object' ? store[key] : store,
      );
    });
  }

  /**
   * Example to open specific page: document.dispatchEvent(new CustomEvent('openSettings', { detail: ['ksuite-mail', 'ksuite-mail-shortcuts'] }));
   */
  @Listen('openSettings', { target: 'document' })
  async openSettingsHandler(ev: CustomEvent<string[]>) {
    if (Object.keys(wcShared.store.userStartConfig).length === 0) return;
    if (wcShared.store.ksuiteBridge?.isConnected) {
      wcShared.store.ksuiteBridge?.sendMessage({
        type: DispatchCustomEventMessageKey,
        selector: 'document',
        eventName: 'openSettings',
        payload: ev.detail,
      });
      return;
    }
    flushStore();
    if (Array.isArray(ev.detail)) {
      const layoutOptions: [TSettingsLayout, TSettingsLayoutPage, object] = [
        undefined,
        undefined,
        undefined,
      ];

      ev.detail.forEach((str) => {
        if (isLayoutAndNotDisabled(str))
          layoutOptions[0] = str as TSettingsLayout;
        else if (isLayoutPageAndNotDisabled(str))
          layoutOptions[1] = str as TSettingsLayoutPage;
        else if (typeof str === 'object') layoutOptions[2] = str;
      });

      const [
        layout = getLayoutFromLayoutPage(layoutOptions[1]) ?? FALL_BACK_LAYOUT,
        page,
        options = undefined,
      ] = layoutOptions;

      const layoutService = getServiceFromLayout(layout);

      const headerOptions: TSettingsHeaderOptions = {};
      let pageLayoutOptions: TSettingsLayoutOptions | undefined = undefined;
      if (options && typeof options === 'object') {
        const {
          selectedId,
          browserSettings,
          pageTitle,
          pageDescription,
          ...rest
        } = options as {
          [k: string]: unknown;
        };

        // Setup options from event
        // - selectedId/orgId/accountId
        if (
          typeof selectedId === 'number' &&
          layoutService instanceof FocusedService
        ) {
          layoutService.defaultSelected = selectedId;
        }
        // - browserSettings
        if (typeof browserSettings === 'object') {
          LayoutStore.browserSettings = browserSettings;
        }
        // - pageTitle
        if (typeof pageTitle === 'string') {
          headerOptions.pageTitle = pageTitle;
        }
        // - pageDescription
        if (typeof pageDescription === 'string') {
          headerOptions.pageDescription = pageDescription;
        }
        // - pageLayoutOptions
        // take all remaining options as pageLayoutOptions
        if (Object.keys(rest).length > 0) {
          pageLayoutOptions = rest;
        }
      }

      setCurrentLayoutAndDefaultLayoutPage(
        layout,
        page?.startsWith(layout) ? page : ALL_LAYOUT_CONFIG[layout].defaultPage,
        headerOptions,
        pageLayoutOptions,
      );
    } else {
      LayoutStore.currentLayout = USER_ACCOUNT;
      LayoutStore.currentLayoutPage =
        ALL_LAYOUT_CONFIG[USER_ACCOUNT].defaultPage;
    }

    this.host.addEventListener('keydown', this.keyPressHandler.bind(this));
    requestAnimationFrame(() => this.host?.focus());
    this.isSettingsOpen = true;
    wcShared?.store?.addObserver('theme', this.onThemeChange);
    globalStore.state.svgSuffix =
      wcShared?.store?.theme === 'dark' ? '-dark' : '';
    window.history.pushState(
      'closeSettings',
      'closeSettings',
      window.location.href,
    );
    (document.activeElement as HTMLElement).blur(); // un-focus open button to let escape listen catch the event if needed. See line 150 for reasons
  }

  @Listen('closeSettings', { target: 'document' })
  closeSettingsHandler() {
    if (!this.isSettingsOpen) return;

    this.host.removeEventListener('keydown', this.keyPressHandler.bind(this));

    this.isSettingsOpen = false;
    document.dispatchEvent(new CustomEvent('closeSettings', { bubbles: true }));
    wcShared?.store?.removeObserver('theme', this.onThemeChange);
  }

  @Listen('popstate', { target: 'window' })
  popStateHandler() {
    if (!this.isSettingsOpen) return;
    this.closeSettingsHandler();
  }

  private keyPressHandler(event: KeyboardEvent) {
    if (!this.isSettingsOpen) return;
    // Check if body is active to prevent closing the modal on a text input
    if (event.key === 'Escape' && document.activeElement === this.host) {
      this.closeSettingsHandler();
    }
    // Stop propagation if the modal is open so underneath element won't catch it
    event.stopPropagation();
  }

  private onThemeChange = () => {
    globalStore.state.svgSuffix =
      wcShared?.store?.theme === 'dark' ? '-dark' : '';
    forceUpdate(this);
  };

  render() {
    return (
      <Host tabindex={-1}>
        {this.isSettingsOpen && (
          <Fragment>
            <div
              class="modal-backdrop"
              onClick={this.closeSettingsHandler.bind(this)}
            />
            <div class="modal-content" onClick={(e) => e.stopPropagation()}>
              <div class={`settings theme-${wcShared?.store?.theme}`}>
                <menu-settings />
                <page-settings />
              </div>
            </div>
          </Fragment>
        )}
      </Host>
    );
  }
}
